// @flow
import React, { useEffect, useState } from 'react';
import {
    defineMessages, injectIntl, IntlShape,
} from 'react-intl';
import { FormCheckmark, FormClose } from 'grommet-icons';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import { Box } from 'grommet';
import { withLeaflet } from 'react-leaflet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import Title from 'esm-web-pro/components/layout/sidePanel/Title';
import Attributes from 'esm-web-pro/components/layout/sidePanel/Attributes';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import CustomAddressForm from 'esm-web-pro/containers/pages/coverage/CustomAddressForm';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import { getAddressTitle, getAddressMarkerAdditionalOptions } from '../Util';
import { ADDRESS_ATTRIBUTE_ICONS as addressIcons } from '../CoverageTab';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitive?: true) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity?: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    object: {
        uuid: string,
        coverage: Object,
        attributes: Array<Object>,
    },
    intl: IntlShape,
    history: { push: (location: string) => void },
    match: Object,
    map: { uuid: string, is_draft: boolean },
    updateObject: Function,
    leaflet: Object,
    editor: BaseEditor,
    panelId: String,
}

const StyledFormCheckmark = styled(FormCheckmark)`
    ${(props) => css`
        stroke: ${props.theme.global.colors.brand};
    `}
`;

const messages = defineMessages({
    'coverage-address-edit-title': {
        id: 'coverage-address-edit-title',
        defaultMessage: 'Edit',
    },
    'address-origin': {
        id: 'address-origin',
        defaultMessage: 'Origin',
    },
    'address-coverage': {
        id: 'address-coverage',
        defaultMessage: 'Coverage',
    },
});

const checkAttribute = (attribute: { value: Object | string }) => {
    let val;
    if (typeof attribute.value === 'object') {
        val = attribute.value?.name;
    } else if (attribute.value === 'attribute-choice-yes') {
        val = <StyledFormCheckmark />;
    } else if (attribute.value === 'attribute-choice-no') {
        val = <FormClose />;
        // eslint-disable-next-line prefer-destructuring
    } else val = attribute.value;
    return val;
};

const ADDRESS_EDIT_KEY = 'ADDRESS_EDIT';

const focusPointCallback = (address) => {
    let focusedLayer;

    return {
        run: (l) => {
            if (l.feature && l.feature.id === address.uuid) {
                const icon = l.getIcon();
                icon.options.className = 'address-marker-highlighted';
                l.setIcon(icon);
                focusedLayer = l;
            }
        },

        cleanUp: () => {
            if (focusedLayer) {
                const icon = focusedLayer.getIcon();
                icon.options.className = 'leaflet-icon-custom';
                focusedLayer.setIcon(icon);
            }
        },
    };
};

const Address = (props: Props) => {
    const [layer, setLayer] = useState(null);
    const {
        history, match, intl, object: address, map: { is_draft: isDraft, uuid: mapUuid }, updateObject, leaflet, editor,
        openPanel, closePanel, panelId,
    } = props;

    const title = getAddressTitle(address);
    const attributes = address.attributes.map((attr: Object) => ({
        label: attr.type.translations[intl.locale].name,
        value: checkAttribute(attr),
        icon: addressIcons[attr.type.code],
    }));

    const focusPoint = focusPointCallback(address);

    const memoizedFocusPoint = React.useCallback(focusPoint, [address.uuid]);

    const getCoverageType = (coverage) => {
        let message;
        if (coverage === 'full') {
            message = intl.messages['addresses-coverage-type-full'];
        } else if (coverage === 'partial') {
            message = intl.messages['addresses-coverage-type-partial'];
        } else {
            message = intl.messages['addresses-coverage-type-outside-operational-region'];
        }
        return message;
    };

    const getCoverageOrigin = (coverageType) => {
        let message;
        if (coverageType === 'inherited_map') {
            message = intl.messages['maptab-coverage-inherited-from-map-boundaries'];
        } else if (coverageType === 'specific') {
            message = intl.messages['maptab-coverage-specific-to-address'];
        } else if (coverageType === 'inherited_polygon') {
            message = intl.messages['maptab-coverage-inherited-from-polygon'];
        } else {
            message = intl.messages['addresses-coverage-type-outside-operational-region'];
        }
        return message;
    };

    attributes.push({
        label: intl.formatMessage(messages['address-coverage']),
        value: getCoverageType(address.coverage.type),
    },
    {
        label: intl.formatMessage(messages['address-origin']),
        value: getCoverageOrigin(address.coverage.origin),
    });

    React.useEffect(() => {
        const pointCoords = address.geom.coordinates;
        leaflet.map.highlightedGeometry = {
            bounds: null, center: [pointCoords[1], pointCoords[0]],
        };
        leaflet.map.panTo([pointCoords[1], pointCoords[0]]);
    }, [address, leaflet.map]);

    useEffect(() => {
        editor.registerLoadingCallback(memoizedFocusPoint);
        leaflet.map.eachLayer((l) => {
            if (l.feature && l.feature.id === address.uuid) {
                setLayer(l);
            }
        });

        return () => {
            editor.unregisterLoadingCallback(memoizedFocusPoint);
        };
    }, [address.uuid, editor, memoizedFocusPoint, leaflet.map]);

    useEffect(() => {
        if (layer) {
            layer.setIcon(getAddressMarkerAdditionalOptions(address, true).icon);
        }
    }, [address, layer]);

    const handleSave = (response: Object) => {
        if (!response) return;
        if (response.attributes) {
            response.attributes = Object.values(response.attributes);
        }
        updateObject(response);
        closePanel(ADDRESS_EDIT_KEY, true);
    };

    const handleEditClose = () => {
        const { closePanel: _closePanel } = props;
        _closePanel(ADDRESS_EDIT_KEY);
    };

    const navigateToTimeline = () => {
        history.push(`${match.url}/timeline/address/${address.id}`.replace('//', '/'));
    };

    const actions = [{
        key: `${address.uuid}-timeline`,
        icon: <TimelineIcon />,
        callback: navigateToTimeline,
        title: intl.formatMessage(actionMessages.timeline),
        color: 'transparent',
    }];

    if (isDraft && !(isEmpty(address.coverage))) {
        actions.push({
            key: `${address.uuid}-edit`,
            icon: <EditIcon />,
            callback: () => openPanel(ADDRESS_EDIT_KEY, '', match.url, true),
            title: intl.formatMessage(actionMessages.edit),
            color: 'transparent',
        });
    }

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            title={title}
        >
            <Box height="100%" justify="between">
                <Box>
                    <UpperContainer>
                        <Title text={title} />
                        <HorizontalLine color="grey" />
                        {attributes && (
                            <Attributes count={1} attributes={attributes} />
                        )}
                        <HorizontalLine color="grey" />
                        <Box direction="row" pad={{ vertical: '15px' }}>
                            <MainActions main actions={actions} />
                        </Box>
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                </Box>
                <BottomContainer actions={<NavigateBackButton />} />

                <SidePane
                    id={ADDRESS_EDIT_KEY}
                    key={address.uuid}
                    size="small"
                    title={title}
                >
                    <CustomAddressForm
                        resourceName="addressAttribute"
                        resourceArguments={['address']}
                        mapId={mapUuid}
                        address={address}
                        objectId={address.uuid}
                        onSave={handleSave}
                        onClose={handleEditClose}
                        title={getAddressTitle(address)}
                    />
                </SidePane>
            </Box>
        </SidePane>

    );
};


const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);

export default withRouter(
    withLeaflet(
        injectIntl(
            connect(null, mapDispatchToProps)(Address),
        ),
    ),
);
